// ─────────────────────────────────────────────────────────────────────────────
// invoicePdf.js — professional invoice PDF generation (jsPDF + jspdf-autotable)
// CC11: header/footer + branding now come from the shared pdfBrand engine (same as
// statements + credit notes), so invoices carry the TENANT's uploaded logo. Only the
// header/footer/branding + text sanitization changed — every invoice field/section
// (FROM, meta, Bill To, line items, per-band VAT, DRS, totals, bank, terms, T&Cs) is
// unchanged. All dynamic text is routed through asciiPdf(A) for WinAnsi-safe rendering.
// ─────────────────────────────────────────────────────────────────────────────
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { COLORS, money, asciiPdf as A, brandedHeader, brandedFooter } from "./pdfBrand";

// ⛔ SEND-0 (26 Oct 2026) — THERE IS DELIBERATELY NO `DEFAULT_BUSINESS` HERE.
//
// It used to hold Msquared's OWN identity — name, the Lusk address, our phone,
// accounts@msquared.ie, VAT number, and a real IBAN and BIC — and the builder
// merged it under the caller's business as `{ ...DEFAULT_BUSINESS, ...business }`.
// Any tenant whose business profile was incomplete therefore printed OUR details
// on THEIR invoice, including our bank account in the pay-into-this-account block.
// Measured on production, 26 Oct 2026: 23 of 23 invoices held an empty
// vat_number, address_line1, phone, email, iban and bank_name, so every single
// one of them fell through to the fallback.
//
// ⛔ DO NOT REINTRODUCE A FALLBACK OBJECT, and do not reach for one in a hurry
// when a field comes through blank. An invoice with blanks is a poor invoice the
// tenant can SEE is poor. An invoice carrying someone else's bank account is a
// payment misdirection, and with someone else's VAT number it is an invalid VAT
// invoice the customer cannot reclaim against. Blanks are strictly safer.
//
// Every block below therefore prints its line or omits the line — never a label
// with nothing after it, and never `undefined`.

const TERMS_LABEL = {
  immediate: "Due on receipt",
  net_7: "Net 7 days",
  net_14: "Net 14 days",
  net_30: "Net 30 days",
  net_60: "Net 60 days",
};

// ⛔ SEND-0 — THESE WERE HARDCODED WITH OUR DETAILS TOO, and that is a second
// leak the fallback was hiding: clause 3 read "remain the property of Msquared"
// and clause 5 "Queries: accounts@msquared.ie" on EVERY invoice, whatever the
// tenant's profile said. Deleting DEFAULT_BUSINESS alone would not have stopped
// those two lines printing on a pilot's invoice.
//
// Now built per-invoice from the tenant's own details, and a clause whose data is
// missing is DROPPED rather than printed half-empty. Numbering is applied after
// filtering so the list always reads 1..n with no gaps.
const termsAndConditions = (B) => [
  "Payment is due within the stated payment terms from the invoice date.",
  "Late payments may incur interest under the EU Late Payment Directive (2012/30/EU).",
  B.name ? `Goods supplied remain the property of ${B.name} until paid for in full.` : null,
  "DRS (Deposit Return Scheme) amounts are refundable on return of eligible containers.",
  B.email
    ? `Please quote the invoice number with your payment. Queries: ${B.email}`
    : "Please quote the invoice number with your payment.",
].filter(Boolean).map((t, i) => `${i + 1}. ${t}`);

// Build (but don't save) the jsPDF document for an invoice.
export function buildInvoiceDoc(invoice, business) {
  const B = business || {};
  const addressLines = String(B.address || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 14; // margin
  const rightX = pageW - M;

  const items = invoice.items || [];

  // Derive totals (fall back to computing from items if the field is absent).
  const lineNet = (it) => (it.total != null ? it.total : (it.quantity || 0) * (it.unit_price || 0));
  const subtotal = invoice.subtotal != null ? invoice.subtotal : items.reduce((s, it) => s + lineNet(it), 0);
  const totalVat = invoice.total_vat != null ? invoice.total_vat : items.reduce((s, it) => s + (it.vat_amount || 0), 0);
  const totalDrs = invoice.total_drs != null ? invoice.total_drs : items.reduce((s, it) => s + (it.drs_amount || 0), 0);
  const grand = invoice.total_amount != null ? invoice.total_amount : subtotal + totalVat + totalDrs;

  // VAT breakdown grouped by rate.
  const vatByRate = {};
  items.forEach((it) => {
    const rate = it.vat_rate || 0;
    const base = lineNet(it);
    const vat = it.vat_amount != null ? it.vat_amount : base * (rate / 100);
    if (!vatByRate[rate]) vatByRate[rate] = { base: 0, vat: 0 };
    vatByRate[rate].base += base;
    vatByRate[rate].vat += vat;
  });

  // ── Header band (shared pdfBrand — tenant logo → monogram → "M²") ────────────
  let y = brandedHeader(doc, { title: "INVOICE", docNumber: invoice.invoice_number, business: B });

  // ── Business details (left) + invoice meta (right) ─────────────────────────
  doc.setTextColor(...COLORS.GREY);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(A("FROM"), M, y);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.NAVY);
  doc.setFontSize(9);
  let by = y + 5;
  // SEND-0: filter() before forEach(). An absent name, email or phone contributes
  // no line at all, and the VAT line is built only when there is a VAT number —
  // printing a bare "VAT: " reads as a rendering bug to the customer receiving it.
  [B.name, ...addressLines, B.email, B.phone, B.vat ? `VAT: ${B.vat}` : null]
    .filter(Boolean)
    .forEach((line) => {
      doc.text(A(line), M, by);
      by += 4.5;
    });

  // invoice meta box on the right
  const metaX = pageW - M - 70;
  doc.setFillColor(...COLORS.LIGHT);
  doc.roundedRect(metaX, y - 4, 70, 30, 2, 2, "F");
  const metaRow = (label, value, row) => {
    const ry = y + 1 + row * 6;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.GREY);
    doc.setFontSize(7.5);
    doc.text(A(label), metaX + 3, ry);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.NAVY);
    doc.setFontSize(9);
    doc.text(A(value || "-"), rightX - 3, ry, { align: "right" });
  };
  metaRow("ISSUE DATE", invoice.issue_date, 0);
  metaRow("DUE DATE", invoice.due_date, 1);
  metaRow("TERMS", TERMS_LABEL[invoice.payment_terms] || invoice.payment_terms, 2);
  metaRow("STATUS", (invoice.status || "").toUpperCase(), 3);

  y = Math.max(by, y + 30) + 4;

  // ── Bill To ────────────────────────────────────────────────────────────────
  doc.setFillColor(...COLORS.LIGHT);
  doc.roundedRect(M, y, pageW - 2 * M, 24, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.GREY);
  doc.setFontSize(7.5);
  doc.text(A("BILL TO"), M + 4, y + 6);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.NAVY);
  doc.setFontSize(11);
  doc.text(A(invoice.customer_name || "-"), M + 4, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.GREY);
  const custLines = [invoice.customer_email, invoice.customer_address, invoice.customer_vat ? `VAT: ${invoice.customer_vat}` : null].filter(Boolean);
  let cy = y + 17;
  custLines.forEach((line) => { doc.text(A(String(line).split("\n")[0]), M + 4, cy); cy += 4.2; });
  y += 30;

  // ── Line items table (autotable) ───────────────────────────────────────────
  const rows = items.map((it) => [
    it.description || "",
    it.unit || "each",
    String(it.quantity ?? 0),
    money(it.unit_price),
    `${it.vat_rate ?? 0}%`,
    (it.drs_amount || 0) > 0 ? money(it.drs_amount) : "-",
    money(lineNet(it)),
  ]);
  const bodyRows = (rows.length ? rows : [["No line items", "", "", "", "", "", ""]]).map((r) => r.map(A));

  autoTable(doc, {
    startY: y,
    head: [["Description", "Unit", "Qty", "Unit Price", "VAT %", "DRS", "Amount"].map(A)],
    body: bodyRows,
    margin: { left: M, right: M },
    styles: { fontSize: 8.5, cellPadding: 2.5, lineColor: [226, 232, 240], lineWidth: 0.1, textColor: COLORS.NAVY },
    headStyles: { fillColor: COLORS.NAVY, textColor: 255, fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 16 },
      2: { cellWidth: 14, halign: "right" },
      3: { cellWidth: 24, halign: "right" },
      4: { cellWidth: 16, halign: "right" },
      5: { cellWidth: 20, halign: "right" },
      6: { cellWidth: 26, halign: "right" },
    },
  });

  y = doc.lastAutoTable.finalY + 6;

  // page-break guard before totals/footer
  const ensureSpace = (needed) => {
    if (y + needed > pageH - 14) { doc.addPage(); y = 18; }
  };
  ensureSpace(70);

  // ── Totals block (right) ───────────────────────────────────────────────────
  const totX = pageW - M - 80;
  const totRow = (label, value, opts = {}) => {
    if (opts.gap) y += 1;
    doc.setFontSize(opts.big ? 10.5 : 9);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setTextColor(...(opts.muted ? COLORS.GREY : COLORS.NAVY));
    doc.text(A(label), totX, y);
    doc.text(A(value), rightX, y, { align: "right" });
    y += opts.big ? 7 : 5.5;
  };
  totRow("Subtotal (excl. VAT)", money(subtotal));
  Object.keys(vatByRate).sort((a, b) => a - b).forEach((rate) => {
    if (vatByRate[rate].vat > 0.0001 || rate > 0) {
      totRow(`VAT @ ${rate}%`, money(vatByRate[rate].vat), { muted: true });
    }
  });
  totRow("Total VAT", money(totalVat), { bold: true });
  if (totalDrs > 0) totRow("DRS Deposit", money(totalDrs), { bold: true });

  // grand total bar
  y += 1;
  doc.setFillColor(...COLORS.BLUE);
  doc.roundedRect(totX - 3, y - 4.5, 80 + 3, 9, 1.5, 1.5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(A("TOTAL DUE"), totX, y + 1.5);
  doc.text(A(money(grand)), rightX - 1, y + 1.5, { align: "right" });
  y += 14;

  // ── Payment details + payment terms (left) ─────────────────────────────────
  // ⛔ SEND-0 — THE BLOCK THAT CARRIED OUR IBAN. It is omitted ENTIRELY when there
  // is nothing to pay into. An empty "PAYMENT DETAILS" heading with nothing under
  // it is worse than no heading — it reads as a rendering bug to the customer —
  // and a half-filled one invites a payment to the wrong place.
  //
  // The test is an account IDENTIFIER, not any bank field: a bank name or a BIC
  // with no IBAN and no account number cannot be paid into, so that is not a
  // payment block, it is decoration. Payment terms still print in the meta box at
  // the top of the page, so dropping this box loses nothing else.
  const payTarget = B.iban || B.bank_account || B.account_number;
  if (payTarget) {
    ensureSpace(60);
    const boxTop = y;
    // Built as a list and drawn in sequence, so a missing line closes up instead
    // of leaving a gap at a hardcoded offset.
    const bankRows = [
      B.bank_account_name ? `Account Name:  ${B.bank_account_name}` : null,
      B.bank_name ? `Bank:  ${B.bank_name}` : null,
      B.iban ? `IBAN:  ${B.iban}` : null,
      B.bank_account || B.account_number ? `Account:  ${B.bank_account || B.account_number}` : null,
      B.bic ? `BIC:  ${B.bic}` : null,
    ].filter(Boolean);
    const boxH = 8 + bankRows.length * 4.5;
    doc.setFillColor(236, 245, 255);
    doc.roundedRect(M, boxTop, pageW - 2 * M, boxH, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.BLUE);
    doc.setFontSize(8);
    doc.text(A("PAYMENT DETAILS"), M + 4, boxTop + 6);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.NAVY);
    doc.setFontSize(8.5);
    let pry = boxTop + 12;
    bankRows.forEach((line) => { doc.text(A(line), M + 4, pry); pry += 4.5; });
    doc.setFont("helvetica", "bold");
    doc.text(A(`Payment Terms:  ${TERMS_LABEL[invoice.payment_terms] || invoice.payment_terms || "-"}`), pageW / 2 + 10, boxTop + 12);
    y = boxTop + boxH + 6;
  }

  // optional notes
  if (invoice.notes) {
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...COLORS.GREY);
    doc.setFontSize(8.5);
    const noteLines = doc.splitTextToSize(A(`Notes: ${invoice.notes}`), pageW - 2 * M);
    doc.text(noteLines, M, y);
    y += noteLines.length * 4.5 + 3;
  }

  // ── Terms & conditions ─────────────────────────────────────────────────────
  ensureSpace(40);
  doc.setDrawColor(...COLORS.LIGHT);
  doc.line(M, y, rightX, y);
  y += 5;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.GREY);
  doc.setFontSize(7.5);
  doc.text(A("TERMS & CONDITIONS"), M, y);
  y += 4.5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  termsAndConditions(B).forEach((t) => {
    const lines = doc.splitTextToSize(A(t), pageW - 2 * M);
    doc.text(lines, M, y);
    y += lines.length * 3.4;
  });

  // ── Footer on every page (shared pdfBrand) ─────────────────────────────────
  brandedFooter(doc, B);

  return doc;
}

// Build and trigger a download.
export function downloadInvoicePDF(invoice, business) {
  const doc = buildInvoiceDoc(invoice, business);
  const safeNum = (invoice.invoice_number || invoice.id || "invoice").replace(/[^\w-]+/g, "_");
  doc.save(`Invoice-${safeNum}.pdf`);
}
